import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button, Field } from '../../components/ui';
import { Checkbox, Select, Textarea } from '../../components/form';
import { useToast } from '../../components/Toast';
import { useFormErrors } from '../../lib/useFormErrors';
import {
  useProjects,
  useSaveCourse,
  useSaveDirection,
  useSaveFlow,
  useSaveProject,
} from '../../api/queries';
import type { Course, Direction, Flow, Project } from '../../api/types';
import { LESSON_HOURS_OPTIONS, lessonsPerStage } from '@doit/shared';

const isoDay = (v: string | null | undefined) => (v ? v.slice(0, 10) : '');

const PRESET_COLORS = ['#0A5A95', '#8E44AD', '#2E6B4A', '#B07A18', '#B42318', '#0F766E'];

/** ფერის ასარჩევი — მიმართულებასა და პროექტში ერთნაირად */
function ColorPicker({
  color,
  onChange,
  error,
}: {
  color: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="block text-[13px] font-medium text-ink-soft">ფერი</span>
      <div className="flex flex-wrap items-center gap-2">
        {PRESET_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-label={c}
            style={{ background: c }}
            className={`size-7 rounded-full transition ${
              color.toLowerCase() === c.toLowerCase()
                ? 'ring-2 ring-ink ring-offset-2'
                : 'hover:scale-110'
            }`}
          />
        ))}
        <input
          type="color"
          value={color}
          onChange={(e) => onChange(e.target.value)}
          className="size-7 cursor-pointer rounded border border-line-strong bg-surface"
          aria-label="სხვა ფერი"
        />
      </div>
      {error && <p className="text-[12px] text-danger">{error}</p>}
    </div>
  );
}

// ============================================================
// პროექტი
// ============================================================

export function ProjectModal({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}) {
  const toast = useToast();
  const save = useSaveProject();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#0A5A95');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    reset();
    setName(project?.name ?? '');
    setCode(project?.code ?? '');
    setDescription(project?.description ?? '');
    setColor(project?.color ?? '#0A5A95');
    setIsActive(project?.isActive ?? true);
  }, [open, project, reset]);

  async function submit() {
    reset();
    try {
      await save.mutateAsync({ id: project?.id, data: { name, code, description, color, isActive } });
      toast.success(project ? 'პროექტი განახლდა' : 'პროექტი დაემატა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={project ? 'პროექტის რედაქტირება' : 'ახალი პროექტი'}
      footer={<Footer onClose={onClose} onSave={() => void submit()} busy={save.isPending} />}
    >
      <div className="space-y-4">
        <Field
          label="დასახელება"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
          placeholder="DoIT"
          autoFocus
        />
        <Field
          label="კოდი"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={fieldErrors.code}
          placeholder="DoIT"
          hint="ნაკადის სახელი ავტომატურად იგება: კოდი + ნომერი (DoIT-4)"
        />
        <ColorPicker color={color} onChange={setColor} error={fieldErrors.color} />
        <Textarea
          label="აღწერა"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={fieldErrors.description}
        />
        <Checkbox label="აქტიური" checked={isActive} onChange={setIsActive} />
      </div>
    </Modal>
  );
}

// ============================================================
// ნაკადი
// ============================================================

export function FlowModal({
  open,
  onClose,
  flow,
}: {
  open: boolean;
  onClose: () => void;
  flow: Flow | null;
}) {
  const toast = useToast();
  const save = useSaveFlow();
  const { data: projects } = useProjects();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [projectId, setProjectId] = useState('');
  const [code, setCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    reset();
    setProjectId(flow?.projectId ?? '');
    setCode(flow?.code ?? '');
    setStartDate(isoDay(flow?.startDate));
    setEndDate(isoDay(flow?.endDate));
    setIsActive(flow?.isActive ?? true);
  }, [open, flow, reset]);

  // სახელი ავტომატურია — მენეჯერი მხოლოდ პროექტსა და ნომერს ირჩევს
  const projectCode = projects?.find((p) => p.id === projectId)?.code ?? '';
  const previewName = projectCode && code ? `${projectCode}-${code}` : '';

  async function submit() {
    reset();
    try {
      await save.mutateAsync({
        id: flow?.id,
        data: { projectId, code, startDate, endDate, isActive },
      });
      toast.success(flow ? 'ნაკადი განახლდა' : 'ნაკადი დაემატა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={flow ? 'ნაკადის რედაქტირება' : 'ახალი ნაკადი'}
      footer={<Footer onClose={onClose} onSave={() => void submit()} busy={save.isPending} />}
    >
      <div className="space-y-4">
        <Select
          label="პროექტი"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          error={fieldErrors.projectId}
          placeholder="აირჩიე პროექტი"
          options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
        <Field
          label="ნომერი"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={fieldErrors.code}
          placeholder="4"
          hint={
            previewName
              ? `ნაკადის სახელი იქნება: ${previewName}`
              : 'სახელი პროექტის კოდისგან იგება, მაგ. DoIT-4'
          }
          autoFocus
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="დაწყება"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            error={fieldErrors.startDate}
          />
          <Field
            label="დასრულება"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            error={fieldErrors.endDate}
          />
        </div>
        <Checkbox label="აქტიური" checked={isActive} onChange={setIsActive} />
      </div>
    </Modal>
  );
}

// ============================================================
// მიმართულება
// ============================================================

export function DirectionModal({
  open,
  onClose,
  direction,
}: {
  open: boolean;
  onClose: () => void;
  direction: Direction | null;
}) {
  const toast = useToast();
  const save = useSaveDirection();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [color, setColor] = useState('#0A5A95');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    reset();
    setName(direction?.name ?? '');
    setCode(direction?.code ?? '');
    setColor(direction?.color ?? '#0A5A95');
    setIsActive(direction?.isActive ?? true);
  }, [open, direction, reset]);

  async function submit() {
    reset();
    try {
      await save.mutateAsync({ id: direction?.id, data: { name, code, color, isActive } });
      toast.success(direction ? 'მიმართულება განახლდა' : 'მიმართულება დაემატა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={direction ? 'მიმართულების რედაქტირება' : 'ახალი მიმართულება'}
      footer={<Footer onClose={onClose} onSave={() => void submit()} busy={save.isPending} />}
    >
      <div className="space-y-4">
        <Field
          label="დასახელება"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
          placeholder="დიზაინი"
          autoFocus
        />
        <Field
          label="კოდი"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={fieldErrors.code}
          placeholder="DSGN"
        />

        <ColorPicker color={color} onChange={setColor} error={fieldErrors.color} />

        <Checkbox label="აქტიური" checked={isActive} onChange={setIsActive} />
      </div>
    </Modal>
  );
}

// ============================================================
// საგანი
// ============================================================

export function CourseModal({
  open,
  onClose,
  course,
  directions,
  projects,
}: {
  open: boolean;
  onClose: () => void;
  course: Course | null;
  directions: Direction[];
  projects: Project[];
}) {
  const toast = useToast();
  const save = useSaveCourse();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [projectId, setProjectId] = useState('');
  const [directionId, setDirectionId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [stageCount, setStageCount] = useState('1');
  const [hoursPerStage, setHoursPerStage] = useState('0');
  const [lessonHours, setLessonHours] = useState('2');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    reset();
    setProjectId(course?.projectId ?? projects[0]?.id ?? '');
    setDirectionId(course?.directionId ?? directions[0]?.id ?? '');
    setName(course?.name ?? '');
    setCode(course?.code ?? '');
    setDescription(course?.description ?? '');
    setStageCount(String(course?.stageCount ?? 1));
    setHoursPerStage(String(course?.hoursPerStage ?? 0));
    setLessonHours(String(course?.lessonHours ?? 2));
    setIsActive(course?.isActive ?? true);
  }, [open, course, directions, projects, reset]);

  // ლექციების რაოდენობა გამოთვლადია — მენეჯერს მაშინვე უჩანს, რას ნიშნავს საათები
  const hours = Number(hoursPerStage) || 0;
  const perLesson = Number(lessonHours) || 0;
  const stages = Number(stageCount) || 1;
  const lessons = hours > 0 && perLesson > 0 ? lessonsPerStage(hours, perLesson) : 0;

  async function submit() {
    reset();
    try {
      await save.mutateAsync({
        id: course?.id,
        data: {
          projectId,
          directionId,
          name,
          code,
          description,
          stageCount,
          hoursPerStage,
          lessonHours,
          isActive,
        },
      });
      toast.success(course ? 'საგანი განახლდა' : 'საგანი დაემატა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={course ? 'საგნის რედაქტირება' : 'ახალი საგანი'}
      footer={<Footer onClose={onClose} onSave={() => void submit()} busy={save.isPending} />}
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="პროექტი"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            error={fieldErrors.projectId}
            placeholder="აირჩიე პროექტი"
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
          <Select
            label="მიმართულება"
            value={directionId}
            onChange={(e) => setDirectionId(e.target.value)}
            error={fieldErrors.directionId}
            placeholder="აირჩიე მიმართულება"
            options={directions.map((d) => ({ value: d.id, label: d.name }))}
          />
        </div>
        <Field
          label="დასახელება"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
          placeholder="Front-end web პროგრამირება"
        />
        <Field
          label="კოდი"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={fieldErrors.code}
          placeholder="FE-WEB"
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="ეტაპები"
            type="number"
            min={1}
            max={10}
            value={stageCount}
            onChange={(e) => setStageCount(e.target.value)}
            error={fieldErrors.stageCount}
          />
          <Field
            label="საათი ეტაპზე"
            type="number"
            min={0}
            value={hoursPerStage}
            onChange={(e) => setHoursPerStage(e.target.value)}
            error={fieldErrors.hoursPerStage}
          />
          <Select
            label="ლექციის ხანგრძლივობა"
            value={lessonHours}
            onChange={(e) => setLessonHours(e.target.value)}
            error={fieldErrors.lessonHours}
            options={LESSON_HOURS_OPTIONS.map((h) => ({
              value: String(h),
              label: `${h} საათი`,
            }))}
          />
        </div>

        {lessons > 0 ? (
          <p className="rounded-md bg-brand-50 px-3 py-2 text-[12px] text-brand-700">
            <strong className="tabular">{lessons}</strong> ლექცია თითო ეტაპზე
            {stages > 1 && (
              <>
                {' '}
                · <strong className="tabular">{lessons * stages}</strong> სულ ({stages} ეტაპი)
              </>
            )}
          </p>
        ) : (
          <p className="rounded-md bg-canvas px-3 py-2 text-[12px] text-ink-mute">
            მიუთითე საათები, რომ ლექციების რაოდენობა გამოითვალოს
          </p>
        )}

        <Textarea
          label="აღწერა"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={fieldErrors.description}
        />
        <Checkbox label="აქტიური" checked={isActive} onChange={setIsActive} />
      </div>
    </Modal>
  );
}

// ============================================================

function Footer({
  onClose,
  onSave,
  busy,
}: {
  onClose: () => void;
  onSave: () => void;
  busy: boolean;
}) {
  return (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
        გაუქმება
      </Button>
      <Button size="sm" onClick={onSave} loading={busy}>
        შენახვა
      </Button>
    </>
  );
}
